/**
 * The one HTTP seam. Every request to the ClimbJobs API goes through `api()`:
 *  - same-origin, cookies included (`cj_session` / `cj_guest` are httpOnly);
 *  - `X-Requested-With: climbjobs` on every request (the backend's CSRF check
 *    rejects any state-changing request without it, API_SPEC.md preamble);
 *  - the RFC 7807-lite error envelope becomes a typed `ApiError`;
 *  - `Idempotency-Key` for POSTs that spend credits or money.
 *
 * Nothing above this layer touches `fetch`.
 */
import type { ApiErrorEnvelope } from './types';

export const API_BASE = '';

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: Record<string, unknown>;
  readonly retryAfterSeconds: number | null;
  readonly requestId: string | null;

  constructor(init: {
    status: number;
    code: string;
    message: string;
    details?: Record<string, unknown>;
    retryAfterSeconds?: number | null;
    requestId?: string | null;
  }) {
    super(init.message);
    this.name = 'ApiError';
    this.status = init.status;
    this.code = init.code;
    this.details = init.details ?? {};
    this.retryAfterSeconds = init.retryAfterSeconds ?? null;
    this.requestId = init.requestId ?? null;
  }

  /** `details.fields` from a 400 `validation_error`, as `{ field: message }`. */
  get fieldErrors(): Record<string, string> {
    const fields = this.details.fields;
    const out: Record<string, string> = {};
    if (fields && typeof fields === 'object' && !Array.isArray(fields)) {
      for (const [k, v] of Object.entries(fields as Record<string, unknown>)) {
        if (typeof v === 'string') out[k] = v;
        else if (Array.isArray(v) && v.length) out[k] = String(v[0]);
      }
    } else if (Array.isArray(fields)) {
      // FastAPI's RequestValidationError shape: [{loc: ['body', 'email'], msg}]
      for (const item of fields as { loc?: unknown[]; msg?: string }[]) {
        const loc = item.loc?.filter((x) => x !== 'body');
        if (loc?.length && item.msg) out[String(loc[loc.length - 1])] = item.msg;
      }
    }
    return out;
  }
}

export function isApiError(e: unknown): e is ApiError {
  return e instanceof ApiError;
}

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  /** JSON body. Ignored when `formData` is set. */
  body?: unknown;
  formData?: FormData;
  query?: Record<string, string | number | boolean | string[] | null | undefined>;
  idempotencyKey?: string;
  signal?: AbortSignal;
  /**
   * Let the request finish after the page that sent it is gone. For a write the
   * UI has already shown as done (ux-bug-pass-3 ticket 02): closing or reloading
   * the tab straight after must not drop it. Small bodies only (browsers cap
   * keepalive requests at 64 KB in flight).
   */
  keepalive?: boolean;
  /** Skip the global 401 broadcast (used by the session bootstrap itself). */
  silent401?: boolean;
}

export const UNAUTHENTICATED_EVENT = 'climbjobs:unauthenticated';

export function buildQuery(query?: RequestOptions['query']): string {
  if (!query) return '';
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null || v === '' || v === false) continue;
    if (Array.isArray(v)) {
      if (v.length) p.set(k, v.join(','));
    } else p.set(k, String(v));
  }
  const s = p.toString();
  return s ? '?' + s : '';
}

/**
 * A short server gap (zero-downtime-deploys ticket 03): the connection failed, or the proxy answered 502, 503 or 504
 * while a container was swapped. The request most likely never reached the app, so waiting and asking again is safe
 * for a read and for a write that is safe to repeat (see `repeatsSafely`).
 */
export function isGapError(e: unknown): boolean {
  return isApiError(e) && (e.status === 0 || e.status === 502 || e.status === 503 || e.status === 504);
}
/** Ten waits of 1, 2, 4, then 8 s each add up to 63 s, the longest gap the client rides out. */
export const GAP_RETRIES = 10;
export const gapRetryDelay = (attempt: number) => Math.min(1000 * 2 ** attempt, 8000);

/**
 * The writes that are safe to send again after a gap, and why:
 *  - every PUT: it sets a value or a flag, so a second copy leaves the same state
 *    (profile fields and flags, insights settings, deck requirements, auto-apply strategy,
 *    certification step, job answers);
 *  - a POST that carries an `Idempotency-Key`: the server answers a repeat with the first result
 *    (tailor, rebuild, deck decision, action retry, checkout).
 * Not repeated: PATCH (a version or status change may already have applied), DELETE (a repeat of one that
 * applied answers 404), and every other POST (a second one would create or spend twice).
 */
export function repeatsSafely(opts: RequestOptions): boolean {
  return opts.method === 'PUT' || (opts.method === 'POST' && !!opts.idempotencyKey);
}

const sleep = (ms: number, signal?: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    if (signal?.aborted) return reject(new DOMException('Aborted', 'AbortError'));
    const t = setTimeout(resolve, ms);
    signal?.addEventListener('abort', () => { clearTimeout(t); reject(new DOMException('Aborted', 'AbortError')); }, { once: true });
  });

/** Run `fn`, and run it again after each wait while it fails on a server gap, for up to about 60 s. */
export async function retryGaps<T>(fn: () => Promise<T>, signal?: AbortSignal): Promise<T> {
  for (let attempt = 0; ; attempt++) {
    try {
      return await fn();
    } catch (e) {
      if (!isGapError(e) || attempt >= GAP_RETRIES) throw e;
      await sleep(gapRetryDelay(attempt), signal);
    }
  }
}

export function api<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  return repeatsSafely(opts) ? retryGaps(() => send<T>(path, opts), opts.signal) : send<T>(path, opts);
}

async function send<T>(path: string, opts: RequestOptions): Promise<T> {
  const url = API_BASE + path + buildQuery(opts.query);
  const headers: Record<string, string> = { 'X-Requested-With': 'climbjobs', Accept: 'application/json' };
  let body: BodyInit | undefined;
  if (opts.formData) body = opts.formData;
  else if (opts.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    body = JSON.stringify(opts.body);
  }
  if (opts.idempotencyKey) headers['Idempotency-Key'] = opts.idempotencyKey;

  let res: Response;
  try {
    res = await fetch(url, { method: opts.method ?? 'GET', headers, body, credentials: 'same-origin', signal: opts.signal, keepalive: opts.keepalive });
  } catch (e) {
    if ((e as Error).name === 'AbortError') throw e;
    throw new ApiError({ status: 0, code: 'network', message: "We couldn't reach ClimbJobs. Check your connection and try again." });
  }

  if (res.status === 204) return undefined as T;

  const text = await res.text();
  let json: unknown = null;
  if (text) {
    try {
      json = JSON.parse(text);
    } catch {
      json = null;
    }
  }

  if (!res.ok) {
    const env = (json ?? {}) as Partial<ApiErrorEnvelope>;
    const retry = res.headers.get('Retry-After');
    const err = new ApiError({
      status: res.status,
      code: env.error?.code ?? defaultCode(res.status),
      message: env.error?.message ?? defaultMessage(res.status),
      details: env.error?.details,
      retryAfterSeconds: retry ? Number(retry) || null : null,
      requestId: res.headers.get('X-Request-Id'),
    });
    if (res.status === 401 && !opts.silent401 && typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(UNAUTHENTICATED_EVENT));
    }
    throw err;
  }
  return json as T;
}

function defaultCode(status: number): string {
  if (status === 401) return 'unauthenticated';
  if (status === 403) return 'forbidden';
  if (status === 404) return 'not_found';
  if (status === 429) return 'rate_limited';
  if (status >= 500) return 'internal_error';
  return 'http_error';
}
function defaultMessage(status: number): string {
  if (status === 404) return 'That was not found.';
  if (status === 429) return 'Too many requests. Please wait a moment.';
  if (status >= 500) return 'ClimbJobs is having trouble right now. Please try again in a minute.';
  return 'Something went wrong.';
}

/** A fresh Idempotency-Key for one logical attempt (kept while that attempt is retried). */
export function newIdempotencyKey(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) return crypto.randomUUID();
  return 'ik_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 12);
}

